import { useState } from 'react';
import { useDevice, useMTCommand } from '@/hooks';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { formatDateTime } from '@/lib/formatters';
import { isIntegratedSensor, resolveSensorTypeName } from '@/lib/sensor-map';
import { findPendingMT } from '@/lib/sensor-commands';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right text-sm font-medium text-slate-800 dark:text-slate-200">{value ?? '—'}</span>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
      <div className="border-b border-slate-200 px-5 py-3 dark:border-navy-700">
        <h2 className="text-base font-semibold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="divide-y divide-slate-50 px-5 dark:divide-navy-800">{children}</div>
    </div>
  );
}

export function DeviceInfoPage() {
  const { selectedDeviceInfo, deviceInfoLoading } = useDevice();
  const [refreshing, setRefreshing] = useState(false);

  const deviceUUID = selectedDeviceInfo?.deviceProperties?.deviceUUID;
  const isIoTCoreConnected = Boolean(selectedDeviceInfo?.deviceProperties?.isIoTCoreConnected);
  const isDirectConnected = Boolean(selectedDeviceInfo?.deviceProperties?.isDirectConnected);
  const isWifiConnected = Boolean(
    (selectedDeviceInfo?.deviceProperties as { isWifiConnected?: boolean } | undefined)?.isWifiConnected,
  );

  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');
  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.SENSOR_MAP_REQUEST);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.SENSOR_MAP_REQUEST);
    } finally {
      setRefreshing(false);
    }
  };

  if (deviceInfoLoading) return <PageSpinner />;

  if (!selectedDeviceInfo) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Info</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">View device details and subscription information</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-navy-600 dark:bg-navy-900">
          <p className="text-sm text-slate-400 dark:text-slate-500">No device selected.</p>
        </div>
      </div>
    );
  }

  const { yachtName, deviceProperties, deviceSubscription, sensorMapList, globalSettings } = selectedDeviceInfo;
  const dp = deviceProperties;
  const sub = deviceSubscription;
  const sensors = sensorMapList ?? [];

  // HD tracking is "active" when the device is connected on a medium (WiFi /
  // cellular) that has HD tracking enabled — mirrors the firmware's effective-
  // rate rule. Satellite has no HD tracking. Display-only here.
  const hdActiveMedium: 'wifi' | 'cellular' | 'satellite' = isWifiConnected
    ? 'wifi'
    : isDirectConnected || isIoTCoreConnected
      ? 'cellular'
      : 'satellite';
  const hdTrackingActive =
    (hdActiveMedium === 'wifi' && Boolean(globalSettings?.wifiHDTrackingEnabled)) ||
    (hdActiveMedium === 'cellular' && Boolean(globalSettings?.cellHDTrackingEnabled));
  const hdTrackingInterval =
    hdActiveMedium === 'wifi'
      ? globalSettings?.wifiHDTrackingIntervalMinutes
      : hdActiveMedium === 'cellular'
        ? globalSettings?.cellHDTrackingIntervalMinutes
        : undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Info</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">View device details and subscription information</p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          Refresh Sensors
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Vessel */}
        <InfoCard title="Vessel">
          <InfoRow label="Vessel Name" value={yachtName?.yachtName} />
        </InfoCard>

        {/* Device */}
        <InfoCard title="Device">
          <InfoRow label="Type" value={dp?.deviceTypeStr ?? String(dp?.deviceType)} />
          <InfoRow label="Serial" value={dp?.deviceSerialStr} />
          <InfoRow label="IMEI" value={dp?.imeiStr} />
          <InfoRow label="Firmware" value={dp?.firmwareRev} />
          <InfoRow label="Build Date" value={formatDateTime(dp?.buildDT)} />
          <InfoRow label="Carrier" value={dp?.networkCarrierStr ?? String(dp?.networkCarrier)} />
          <InfoRow
            label="Connection"
            value={
              <span className="inline-flex items-center gap-2">
                <StatusBadge
                  label={isIoTCoreConnected ? 'Connected' : 'Not Connected'}
                  variant={isIoTCoreConnected ? 'success' : 'neutral'}
                  dot
                />
                {isWifiConnected && <StatusBadge label="WiFi" variant="info" dot />}
              </span>
            }
          />
          <InfoRow
            label="HD Tracking"
            value={
              <StatusBadge
                label={hdTrackingActive ? `Active · ${hdTrackingInterval} min` : 'Off'}
                variant={hdTrackingActive ? 'success' : 'neutral'}
                dot
              />
            }
          />
        </InfoCard>

        {/* Subscription */}
        <InfoCard title="Subscription">
          <InfoRow
            label="Plan"
            value={
              sub?.subscriptionName
                ? sub.subscriptionName
                : (!sub?.subscriptionType || (sub.subscriptionType as unknown) === 0 || sub.subscriptionType === 'NONE')
                  ? 'No Plan'
                  : String(sub.subscriptionType)
            }
          />
          {sub?.dataAllowance != null && sub.dataAllowance > 0 && (
            <InfoRow label="Data Allowance" value={`${sub.dataAllowance}`} />
          )}
          {sub?.onDemandLimit != null && sub.onDemandLimit > 0 && (
            <InfoRow label="On-Demand Limit" value={`${sub.onDemandLimit}`} />
          )}
          {sub?.globalGeofencingEnabled && (
            <InfoRow label="Global Geofencing" value={<StatusBadge label="Enabled" variant="success" />} />
          )}
          {sub?.fleetManagementEnabled && (
            <InfoRow label="Fleet Management" value={<StatusBadge label="Enabled" variant="success" />} />
          )}
          {sub?.vmsEnabled && (
            <InfoRow label="VMS" value={<StatusBadge label="Enabled" variant="success" />} />
          )}
        </InfoCard>
      </div>

      {/* Connected Sensors — wireless/external only */}
      {sensors.filter((entry) => !isIntegratedSensor(entry)).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
          <div className="border-b border-slate-200 px-5 py-3 dark:border-navy-700">
            <h2 className="text-base font-semibold text-slate-800 dark:text-white">Connected Sensors</h2>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-navy-800">
            {sensors.filter((entry) => !isIntegratedSensor(entry)).map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{entry.sensorName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{resolveSensorTypeName(entry.sensorType)}</p>
                </div>
                <StatusBadge label="Wireless" variant="success" dot />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
